import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { useLazyGetDispatcherUsersQuery } from '@/services/dispatcher/dispather'
import { useLazyGetExecutivesUsersQuery } from '@/services/executives/executives'

import { getUser } from '../../features/auth/authSlice'
import { useUpdateChatMutation } from '../../services/chats/chats'
import { useLazyGetAllUsersQuery } from '../../services/tenant/users'
import { UserRoleEnum } from '../../types'
import type { IChat } from '../../types/chats'
import type { ITeamMember } from '../../types/tenant/team.types'
import Button from '../common/Button'
import Input, { InputStyles } from '../common/Input'
import Modal from '../common/Modal'
import SearchInputWidthDrop from '../common/SearchInputWidthDrop'
import type { UserPartListProps } from '../common/SelectedUsersList'
import SelectedUsersList from '../common/SelectedUsersList'

interface IEditChatModalProps {
    isOpen: boolean
    onClose: () => void
    chat: IChat
}

const EditChatModal = ({ isOpen, onClose, chat }: IEditChatModalProps) => {
    const [getUsers] = useLazyGetAllUsersQuery()
    const [getDispatcherUsers] = useLazyGetDispatcherUsersQuery()
    const [getExecutivesUsers] = useLazyGetExecutivesUsersQuery()
    const [updateChat] = useUpdateChatMutation()
    const [selectedUsers, setSelectedUsers] = useState<UserPartListProps[]>([])
    const [name, setName] = useState('')
    const user = useSelector(getUser)

    const handelClose = () => {
        onClose()
    }

    useEffect(() => {
        setName(chat?.name || '')
        setSelectedUsers(
            chat?.users?.map((user) => ({
                id: user.id,
                src: user.photo,
                name: user.fullName,
                role: user.roles[0],
            })) || [],
        )
    }, [chat])

    const removeSelectedUser = (id: number) => {
        setSelectedUsers(selectedUsers.filter((user) => user.id !== id))
    }

    const selectUserSearch = (user: ITeamMember) => {
        if (!selectedUsers.find((u) => u.id === user.id)) {
            setSelectedUsers((prev) => [
                ...prev,
                {
                    id: user.id,
                    name: user.fullName,
                    email: user.email,
                    src: user.photo,
                    role: user.roles[0],
                },
            ])
        }
    }

    const onSubmit = async () => {
        if (!user) {
            toast.error('User not found')
            return
        }

        if (!name.trim()) {
            toast.error('Name is required.')
            return
        }

        if (selectedUsers.length === 0) {
            toast.error('At least one member must be added.')
            return
        }

        try {
            const participants: number[] = selectedUsers.map((user) => user.id)
            if (participants.indexOf(user.id) === -1) {
                participants.push(user.id)
            }
            const res = await updateChat({
                id: chat.id,
                data: {
                    name,
                    participants,
                },
            }).unwrap()

            if (res?.success) {
                toast.success(res?.message || 'Updated successfully!')
                onClose()
            } else {
                toast.error(res?.message || 'Something went wrong')
            }
        } catch (error) {
            console.log(error)
            toast.error('Something went wrong')
        }
    }

    const fetcherMap = {
        [UserRoleEnum.TENANT_ADMIN]: getUsers,
        [UserRoleEnum.DISPATCHER]: getDispatcherUsers,
        [UserRoleEnum.EXECUTIVES]: getExecutivesUsers,
    }

    const activeRole = window.localStorage.getItem('activeRole')
    const role =
        activeRole && Object.keys(fetcherMap).includes(activeRole)
            ? activeRole
            : undefined
    if (!role) return null

    const fetcher = fetcherMap[role as keyof typeof fetcherMap]

    return (
        <Modal isOpen={isOpen} onClose={handelClose} title={'Edit Chat'}>
            <div className="modal__body p-0">
                <div className="custom-form">
                    <div className="custom-form-body">
                        <div className="row gutters-16">
                            <div className="col-12">
                                <Input
                                    inputStyle={InputStyles.outline}
                                    label="Chat Name"
                                    placeholder="Enter Name..."
                                    value={name}
                                    helpText="The chat name is displayed only if there are more than 2 participants in the chat"
                                    onChange={(e) => setName(e.target.value)}
                                />
                            </div>
                            <div className="col-12">
                                <div className="search-widget overflow-visible">
                                    <div className="search-widget--head">
                                        <SearchInputWidthDrop
                                            placeholder="Add users by name or email..."
                                            label="Users"
                                            fetchFunk={fetcher}
                                            selectedItems={selectedUsers.map(
                                                (user) => user.id,
                                            )}
                                            onSelected={selectUserSearch}
                                        />
                                    </div>
                                    <div className="search-widget--body">
                                        <SelectedUsersList
                                            list={selectedUsers?.filter(
                                                (u) => u.id !== user?.id,
                                            )}
                                            removeAction={removeSelectedUser}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="custom-form-footer justify-end">
                        <div className="flex-group flex-group--xs">
                            <Button
                                type="button"
                                onClick={onSubmit}
                                size="lg"
                                variant="primary-gradient"
                            >
                                <span>Save Changes</span>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default EditChatModal
